// medplum-photo.js — ONLINE-PRIMARY clinical-photo upload (Card E).
//
// The 15MB anonymous clinical-photo upload uses the existing magic-link/device
// gateway. The gateway derives the Encounter and Patient from that credential,
// then stores Binary + DocumentReference. No shared Medplum client secret ships
// in the browser/tablet bundle or enters a Bot archive.
//
// What stays behind the existing Express gateway:
//   • Binary bytes + the DocumentReference create, after caller authorization.
//   • Vision auto-classify + lab-value extraction (server/opd/clinical-photos.mjs
//     classifyDocumentImage / recordExtractedLabValues) need the OpenAI key AND
//     a read of the bytes — they CANNOT run on a create-only device and are
//     DROPPED from this path (Card E decision). Kiosk/self photos are
//     clinical photographs; the shapers' category inference already lands them
//     on 'clinical-photo' without vision.
import { apiUrl } from './net-base.js';
import { authHeader } from './medplum-client.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isMedplumUuid(v) { return UUID_RE.test(String(v || '')); }

// Upload ONE clinical photo through the existing bounded multipart endpoint.
// The gateway creates Binary first, then the DocumentReference that points at it.
// Medplum rewrites the Binary attachment URL on an authorized clinical read.
//
// Success = both creates returned ids; no read-back is attempted. FAIL LOUD: a
// missing/non-UUID patient or encounter id would write
// an orphaned photo that no doctor's RLS-scoped search could ever surface, so
// it throws instead (the capture UI shows its upload-failed chip and keeps the
// camera live for a retry).
export async function uploadClinicalPhoto({
  file, patientId, encounterId, label,
  region, reason, planId, source, filename, token,
}) {
  if (!file) throw new Error('uploadClinicalPhoto: file required');
  if (!isMedplumUuid(patientId)) {
    throw new Error(`uploadClinicalPhoto: patientId must be the Medplum Patient UUID (got "${patientId}")`);
  }
  if (!isMedplumUuid(encounterId)) {
    throw new Error(`uploadClinicalPhoto: encounterId must be the Medplum Encounter UUID (got "${encounterId}")`);
  }
  const title = label || region || 'Clinical photo';
  const data = new FormData();
  data.set('photo', file, filename || file.name || `${title}.jpg`);
  data.set('label', title);
  data.set('region', region || 'other');
  if (reason) data.set('reason', String(reason));
  if (planId) data.set('planId', String(planId));
  data.set('source', source || 'kiosk');
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  const response = await fetch(apiUrl(`/api/v1/opd/encounters/${encodeURIComponent(encounterId)}/clinical-photo${query}`), {
    method: 'POST',
    // Human staff must reach the gateway with their own Medplum bearer so the
    // staff branch writes through RLS. Anonymous magic-link and paired-device
    // calls deliberately omit any stored staff session so their existing
    // gateway credentials remain the sole authorization boundary.
    headers: source === 'doctor' ? { ...authHeader() } : {},
    body: data,
  });
  const out = await response.json().catch(() => ({}));
  if (!response.ok || out.ok === false) {
    throw new Error(out.error || `clinical photo upload failed (${response.status})`);
  }
  return out;
}
